import {
	Input,
	InputChangeEvent,
	InputProps,
	NumericTextBox,
	NumericTextBoxProps,
} from "@progress/kendo-react-inputs";
import { NumberFormatOptions } from "@progress/kendo-intl";
import React, { FC, useState } from "react";
import { COLOR, ICON_NAMES } from "../utils/constant";
import { GridCustomCellProps } from "@progress/kendo-react-grid";
import { ItemLabel } from "./Topography";
import { Label } from "@progress/kendo-react-labels";

interface CustomInputProp extends InputProps {
	onChange: (value: any) => void;
	isError?: boolean;
	inputStyle?: React.CSSProperties;
	containerStyle?: React.CSSProperties;
	labeStyle?: React.CSSProperties;
}

export const CustomInput: FC<CustomInputProp> = ({
	onChange,
	isError = false,
	inputStyle,
	containerStyle,
	...props
}) => {
	const handleOnChange = (e: any) => {
		const value = e.target.value;
		onChange(value);
	};
	const className = isError ? "input-container error" : "input-container";
	const mergedStyle = { width: "100%", ...inputStyle };

	return (
		<div style={{ width: "100%", ...containerStyle }}>
			<Input
				style={mergedStyle}
				onChange={handleOnChange}
				value={props.value}
				className={className}
				type={props.type}
				{...props}
			/>
		</div>
	);
};

interface CustomCheckboxProp {
	label: string;
	value?: boolean | string;
	checked?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	onChange?: (value: boolean) => void;
	onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
	labeStyle?: React.CSSProperties;
	inputStyle?: React.CSSProperties;
	containerStyle?: React.CSSProperties;
	id?: string;
	name?: string;
	tabIndex?: number;
	title?: string;
	size?: "sm" | "md" | "lg";
}

export const CustomCheckbox: FC<CustomCheckboxProp> = ({
	onChange,
	onClick,
	inputStyle,
	containerStyle,
	label,
	disabled = false,
	readOnly = false,
	size = "md",
	...props
}) => {
	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (disabled || readOnly) return;
		onClick?.(e);
		const current =
			props.value === "true" || props.value === true || props.checked;
		onChange?.(!current);
	};

	const isChecked =
		props.value === "true" || props.value === true || props.checked;
	const iconName = isChecked ? ICON_NAMES.Checkbox : ICON_NAMES.CheckboxBlack;

	const sizeMap = { sm: "26px", md: "32px", lg: "38px" };

	let baseStyle: React.CSSProperties = {
		boxSizing: "border-box",
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		padding: "5px 10px",
		gap: "5px",
		height: sizeMap[size],
		background: "#F7F9FA",
		border: isChecked
			? `1.4px solid ${COLOR.Green}`
			: "1.4px solid transparent",
		borderRadius: "4px",
		whiteSpace: "nowrap",
		flexShrink: 0,
		cursor: disabled ? "not-allowed" : readOnly ? "default" : "pointer",
		opacity: disabled ? 0.5 : 1,
		userSelect: "none",
		...containerStyle,
	};

	return (
		<div
			style={baseStyle}
			onClick={handleClick}
			id={props.id}
			title={props.title}
			tabIndex={disabled ? -1 : (props.tabIndex ?? 0)}
			role="checkbox"
			aria-checked={isChecked}
			aria-disabled={disabled}
			aria-readonly={readOnly}
			onKeyDown={(e) => {
				if ((e.key === " " || e.key === "Enter") && !disabled && !readOnly) {
					e.preventDefault();
					onChange?.(!isChecked);
				}
			}}
		>
			<Label
				style={{
					...props.labeStyle,
					marginBottom: 0,
					lineHeight: 1,
					cursor: "inherit",
				}}
			>
				{label}
			</Label>

			<img
				src={`/icon/${iconName}.svg`}
				alt={iconName}
				style={{
					height: "16px",
					width: "16px",
					cursor: "inherit",
					flexShrink: 0,
					...inputStyle,
				}}
			/>
		</div>
	);
};
interface CustomNumberInputProp extends NumericTextBoxProps {
	onChange: (value: any) => void;
	isError?: boolean;
	inputStyle?: React.CSSProperties;
	containerStyle?: React.CSSProperties;
	numberFormat?: string | NumberFormatOptions | undefined;
	value?: any;
}

export const CustomNumberInput: FC<CustomNumberInputProp> = ({
	onChange,
	isError = false,
	inputStyle,
	containerStyle,
	style,
	className,
	...props
}) => {
	const handleOnChange = (e: InputChangeEvent) => {
		const value = e.target.value;
		onChange(value);
	};
	const mergedStyle = { width: "100%", ...(style || {}), ...inputStyle };
	const mergedClassName = [className, isError ? "error" : ""]
		.filter(Boolean)
		.join(" ");

	return (
		<div style={{ width: "100%", ...containerStyle }}>
			<NumericTextBox
				style={mergedStyle}
				className={mergedClassName || undefined}
				placeholder="please enter value"
				label="Weight"
				// defaultValue={77}
				format=""
				onChange={(e) => {
					handleOnChange(e as any);
				}}
				{...props}
				// onChange={handleOnChange}
			/>
		</div>
	);
};

export const GridInput: FC<GridCustomCellProps> = ({
	td2Props,
	tdProps,
	...props
}) => {
	return <td {...tdProps}> {props.children}</td>;
};

export const PasswordInput: FC<
	CustomInputProp & { setPasswordValidated: (isValidated: boolean) => void }
> = ({
	onChange,
	type,
	isError,
	className,
	setPasswordValidated,
	containerStyle,
	...props
}) => {
	const [showPassword, setShowPassword] = useState(false);

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	const handleOnChange = (e: InputChangeEvent) => {
		const value = e.target.value;
		onChange(value);
	};

	const icon = showPassword ? ICON_NAMES.VisibilityOff : ICON_NAMES.Visibility;
	const _className = isError ? "input-container error" : "input-container";

	return (
		<div className={_className} style={{ ...containerStyle }}>
			<Input
				style={{ border: "none" }}
				type={showPassword ? "text" : "password"}
				onChange={handleOnChange}
				value={props.value}
				{...props}
			/>

			<span onClick={togglePasswordVisibility} style={{ paddingRight: "3px" }}>
				<img src={`/icon/${icon}.svg`} alt={icon} />
			</span>
		</div>
	);
};
